// Which customer accounts this staffer has unlocked, and the OTP tokens that
// prove it.
//
// The OTP is a consent boundary: staff can't open a customer's account until
// someone with access to that customer's own inbox reads back a code. The
// backend enforces it (requireAccountToken in squish-crm-backend/server.js);
// this module is the client half — it holds the tokens the OTP exchange
// returned and attaches them to every request, so the boundary is one unlock
// per account rather than one unlock per screen.
//
// Backed by sessionStorage, deliberately:
//   - The token is a 12h JWT, but React state dies on refresh. Holding it only
//     in state meant a reload re-prompted for an OTP the customer had already
//     approved — the tax that makes people want the boundary switched off.
//   - sessionStorage is per-tab and cleared when the tab closes, so an unlocked
//     account does not outlive the session on a shared machine. localStorage
//     would persist it across browser restarts, which is the wrong trade for a
//     consent grant.
//
// Tokens are opaque here. The client never reads what's inside one and never
// tells the server which accounts are unlocked — it sends the signed tokens and
// the server derives the account ids. A tampered or expired token simply fails
// verification and the account reads as locked.

const KEY = 'squish.crm.accountTokens';

function read() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    // Corrupt entry, or storage unavailable (private mode, blocked cookies).
    // Behave as "nothing unlocked" rather than throwing inside getHeaders and
    // taking every request down with it.
    return {};
  }
}

function write(map) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // Storage full or unavailable — the in-memory result of this unlock still
    // works for the current page, it just won't survive a reload.
  }
}

// Which of those unlocks are VIEW-ONLY.
//
// The token stays opaque: this is what the server SAID when it issued it
// (`readOnly: true` from POST /api/otp/view-only), kept beside it. It decides
// nothing — the server ignores a view-only token on any write regardless — it
// only lets the page stop offering changes it knows will be refused, and offer
// "unlock to make changes" instead.
const VIEW_ONLY_KEY = 'squish.crm.viewOnlyAccounts';

function readViewOnly() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(VIEW_ONLY_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function writeViewOnly(ids) {
  try { sessionStorage.setItem(VIEW_ONLY_KEY, JSON.stringify(ids)); } catch { /* see write() */ }
}

function setViewOnly(accountId, on) {
  const ids = readViewOnly().filter(id => id !== accountId);
  if (on) ids.push(accountId);
  writeViewOnly(ids);
}

/**
 * Record an unlock token for an account. `readOnly` marks one that came from
 * the view-only open. A later full unlock (a customer code, the override)
 * replaces it and clears the mark.
 */
export function unlockAccount(accountId, token, { readOnly = false } = {}) {
  if (!accountId || !token) return;
  const map = read();
  map[accountId] = token;
  write(map);
  setViewOnly(accountId, readOnly === true);
  notify();
}

/** Is this account open view-only (unlocked, but not for changes)? */
export function isViewOnlyUnlock(accountId) {
  return !!accountId && !!read()[accountId] && readViewOnly().includes(accountId);
}

/** Forget one account (e.g. the server told us its token is no longer valid). */
export function lockAccount(accountId) {
  const map = read();
  if (!(accountId in map)) return;
  delete map[accountId];
  write(map);
  setViewOnly(accountId, false);
  notify();
}

/** Drop every unlock — used on sign-out surfaces. */
export function lockAll() {
  try { sessionStorage.removeItem(KEY); } catch { /* see write() */ }
  try { sessionStorage.removeItem(VIEW_ONLY_KEY); } catch { /* see write() */ }
  notify();
}

/** Has this account been unlocked in this tab? */
export function isUnlocked(accountId) {
  return !!accountId && !!read()[accountId];
}

/** The token for one account, or null. */
export function tokenFor(accountId) {
  return (accountId && read()[accountId]) || null;
}

/**
 * Every token we hold, for the `x-account-tokens` request header.
 *
 * The server verifies each one and unions the account ids. Sending the whole
 * set rather than the one token a given route needs is what lets the global ~
 * console — which is not bound to any single account — answer about every
 * account the staffer has open, while a route still only admits its own.
 */
export function accountTokensHeader() {
  const tokens = Object.values(read()).filter(Boolean);
  return tokens.length ? tokens.join(',') : null;
}

// ─── Change notification ─────────────────────────────────────────────────────
// Components that gate on unlock state (Customer Lookup) re-render when an
// unlock lands. Same shape as lib/viewContext's subscribe.

const listeners = new Set();

function notify() {
  for (const fn of listeners) {
    try { fn(); } catch (e) { console.warn('[accountSession] listener failed:', e); }
  }
}

export function subscribeAccountSession(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
